import { Box, Stack } from "@mui/material";
import { GetProcessesResults, useGetProcesses } from "~/queries/useGetProcesses";
import { useBulkUpdateFiles } from "~/queries/useBulkUpdateFiles";
import { useBulkUpdateProcesses } from "~/queries/useBulkUpdateProcesses";
import { useViewPreference } from "~/hooks/use-view-preference";
import { DragDropContext, Droppable, DropResult } from "react-beautiful-dnd";
import { FILE_VIEW_PREFERENCE_KEYS } from "~served/configs";
import { ProcessTable } from "./process-table";
import { ProcessCard, ProcessLoadingCard } from "./process-card";
import { CUSTOM_FIELD_TYPE, Process } from "~/types/__generated/gql/graphql";
import { ProcessAdd } from "./process-add";
import { useMemo } from "react";
import { useFileSettings } from "~/hooks/use-file-settings";
import { $dayjs, DATEYEARMONTH_FORMAT } from "~/libs/dayjs";
export const Files = () => {
  const {
    data: processes = [],
    isLoading
  } = useGetProcesses();
  const bulkUpdateFiles = useBulkUpdateFiles();
  const bulkUpdateProcesses = useBulkUpdateProcesses();
  const viewPreference = useViewPreference();
  const {
    fileSearch,
    hideProcess
  } = useFileSettings();
  const isMatchFileSearch = (file: GetProcessesResults[0]["_files"][0]) => {
    if (!fileSearch) return true;
    const filePrefix = file.counterConfigs?.file?._counter.isPredefinedFormat ? `${$dayjs(file.createdAt).format(DATEYEARMONTH_FORMAT)}` : file.counterConfigs?.file?._counter.prefix;
    const fileNumber = `${filePrefix}-${file.counterConfigs?.file?.index ? String(file.counterConfigs.file?.index || 0).padStart(file.counterConfigs.file?._counter?.suffix_digit || 0, "0") : ""}${file.fileSuffixComment ? `-${file.fileSuffixComment}` : ""}`;
    const invoicePrefix = file.counterConfigs?.invoice?._counter.isPredefinedFormat ? `${$dayjs(file.createdAt).format(DATEYEARMONTH_FORMAT)}` : file.counterConfigs?.invoice?._counter.prefix;
    const invoiceNumber = `${invoicePrefix}-${file.counterConfigs?.invoice?.index ? String(file.counterConfigs.invoice?.index || 0).padStart(file.counterConfigs.invoice?._counter?.suffix_digit || 0, "0") : ""}${file.receiptSuffixComment ? `-${file.receiptSuffixComment}` : ""}`;
    const isMatchAssignee = fileSearch.assignees?.length > 0 ? file._assignees.some(a => fileSearch.assignees.includes(a._id)) : true;
    const isMatchCreator = fileSearch.creators?.length > 0 ? fileSearch.creators.includes(file._user._id) : true;
    const isMatchedClient = fileSearch.clients?.length > 0 ? file._client ? fileSearch.clients.includes(file._client._id) : false : true;
    const isMatchedAgent = fileSearch.agents?.length > 0 ? !file._agent ? false : fileSearch.agents.includes(file._agent._id) : true;
    const fileOrderStatus = file.customFields.find(f => f._customField.name.toLowerCase() === "order status" && (f._customField.type === CUSTOM_FIELD_TYPE.SELECT_MULTI || f._customField.type === CUSTOM_FIELD_TYPE.SELECT_SINGLE));
    const isMatchedOrderStatus = fileSearch.orderStatuses?.length > 0 ? !fileOrderStatus ? false : (fileOrderStatus.selectValues ?? []).some(s => fileSearch.orderStatuses.includes(s)) : true;
    const fileReceiptPaymentStatus = file._receipt ? file._receipt.isFullyPaid ? "paid" : file._receipt.payments.length > 0 ? "partial" : "unpaid" : "unpaid";
    const isMatchedPaymentStatus = fileSearch.paymentStatuses?.length > 0 ? fileSearch.paymentStatuses.includes(fileReceiptPaymentStatus) : true;
    const isMatchFile = fileSearch.files?.length > 0 ? (fileSearch.files ?? []).some(f => fileNumber.includes(f)) : true;
    const isMatchInvoice = fileSearch.invoices?.length > 0 ? (fileSearch.invoices ?? []).some(f => invoiceNumber.includes(f)) : true;
    return isMatchAssignee && isMatchCreator && isMatchedClient && isMatchedAgent && isMatchedOrderStatus && isMatchedPaymentStatus && isMatchFile && isMatchInvoice;
  };
  const filteredProcesses = useMemo(() => {
    return processes.filter(process => {
      if (!hideProcess) return true;
      if (!hideProcess.selectedProcesses || hideProcess.selectedProcesses?.length === 0) return true;
      return !hideProcess.selectedProcesses.includes(process._id);
    }).map(process => ({
      ...process,
      _files: process._files.filter(file => isMatchFileSearch(file))
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processes, fileSearch, hideProcess]);
  const handleDragEndFile = async ({
    source,
    destination,
    draggableId: fileId
  }: DropResult): Promise<void> => {
    // Dropped outside the column
    if (!destination) {
      return;
    }

    // File has not been moved
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }
    if (source.droppableId === destination.droppableId) {
      // Same process, different fileing index
      const fileProcess = processes.find(process => process._id === destination.droppableId);
      if (!fileProcess) return;
      const newFiles = Array.from(fileProcess._files);
      const [removed] = newFiles.splice(source.index, 1);
      newFiles.splice(destination.index, 0, removed);
      await bulkUpdateFiles.mutateAsync({
        inputs: newFiles.map((item, index) => ({
          id: item._id,
          input: {
            sortingIndex: index
          }
        }))
      });
    } else {
      // Different process, different fileing index
      const movedFile = processes.map(process => process._files).flat().find(file => file._id === fileId);
      if (!movedFile) return;
      const destinationProcess = processes.find(process => process._id === destination.droppableId);
      if (!destinationProcess) return;
      const newDestinationFiles = Array.from(destinationProcess._files);
      newDestinationFiles.splice(destination.index, 0, movedFile);
      await bulkUpdateFiles.mutateAsync({
        inputs: newDestinationFiles.map((item, index) => ({
          id: item._id,
          input: {
            sortingIndex: index,
            process: item._id === fileId ? destination.droppableId : undefined
          }
        }))
      });
    }
  };
  const handleDragEndProcess = async ({
    source,
    destination
  }: DropResult): Promise<void> => {
    // Dropped outside the column
    if (!destination) {
      return;
    }

    // Process has not been moved
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    // Process moved to a different fileing index
    const newProcesses = Array.from(processes);
    const [removed] = newProcesses.splice(source.index, 1);
    newProcesses.splice(destination.index, 0, removed);
    await bulkUpdateProcesses.mutateAsync({
      inputs: newProcesses.map((item, index) => ({
        id: item._id,
        input: {
          sortingIndex: index
        }
      }))
    });
  };
  const handleDragEnd = (result: DropResult) => {
    const {
      type
    } = result;
    if (type === "FILE") void handleDragEndFile(result);
    if (type === "PROCESS") void handleDragEndProcess(result);
  };
  return <>
			{viewPreference.fileViewPreference === FILE_VIEW_PREFERENCE_KEYS.TABLE_VIEW ? <Box sx={{
      display: "flex",
      flexGrow: 1,
      flexShrink: 1,
      overflowX: "auto",
      overflowY: "hidden",
      px: 3,
      py: 3
    }}>
					<ProcessTable processes={filteredProcesses} />
				</Box> : <DragDropContext onDragEnd={handleDragEnd}>
					{/* // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      @ts-ignore */}
					<Box sx={{
        display: "flex",
        flexGrow: 1,
        flexShrink: 1,
        overflowX: "auto",
        overflowY: "hidden",
        px: 3,
        py: 3
      }}>
						<Stack direction="row" spacing={3} alignItems="flex-start">
							<Droppable type="PROCESS" droppableId="process-columns" direction="horizontal">
								{/* // eslint-disable-next-line @typescript-eslint/ban-ts-comment
         @ts-ignore */}
								{processDroppableProvider => <Stack alignItems="flex-start" direction="row" spacing={3} ref={processDroppableProvider.innerRef}>
										{isLoading ? new Array(5).fill(null).map((_, index) => <ProcessLoadingCard key={index} />) : filteredProcesses.map(process => <ProcessCard key={process._id} process={process as Process} />)}
										{processDroppableProvider.placeholder}
									</Stack>}
							</Droppable>
							{!isLoading && <ProcessAdd />}
						</Stack>
					</Box>
				</DragDropContext>}
		</>;
};